import React, { useState, useEffect, useCallback } from 'react';
import {
  QrCodeIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentIcon,
  PencilSquareIcon,
  TrashIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { generalApi } from '../../api/generalApi';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import ResponsiveModal from '../../components/ui/ResponsiveModal';
import Loading from '../../components/ui/Loading';

const unwrapList = (res) => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
};

const unwrapRecord = (res) => res?.data || res;

const formatDate = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('tr-TR');
  } catch {
    return value;
  }
};

const QrCodePage = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [createForm, setCreateForm] = useState({ title: '', targetUrl: '' });
  const [editForm, setEditForm] = useState({ id: null, title: '', targetUrl: '' });
  const [previewData, setPreviewData] = useState(null);

  const loadRecords = useCallback(async () => {
    setError('');
    try {
      const res = await generalApi.getLinkQrCodes();
      setRecords(unwrapList(res));
    } catch (err) {
      setError(err.message || 'Link QR kayıtları yüklenemedi.');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const validateUrl = (url) => {
    const trimmed = url.trim();
    if (!trimmed) return 'Lütfen bir URL girin.';
    if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
      return 'URL http:// veya https:// ile başlamalıdır.';
    }
    return '';
  };

  const openCreate = () => {
    setCreateForm({ title: '', targetUrl: '' });
    setFormError('');
    setCreateOpen(true);
  };

  const openEdit = (record) => {
    setEditForm({
      id: record.id,
      title: record.title || '',
      targetUrl: record.target_url || '',
    });
    setFormError('');
    setEditOpen(true);
  };

  const openPreview = async (record) => {
    setPreviewData({ ...record, loading: true, imageBase64: null });
    setPreviewOpen(true);
    try {
      const res = await generalApi.getLinkQrCode(record.id);
      const data = unwrapRecord(res);
      setPreviewData({
        ...record,
        ...data,
        loading: false,
        imageBase64: data.image_base64,
        targetUrl: data.target_url,
      });
    } catch (err) {
      setPreviewData((prev) => ({ ...prev, loading: false, error: err.message }));
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const urlError = validateUrl(createForm.targetUrl);
    if (urlError) {
      setFormError(urlError);
      return;
    }
    setFormError('');
    setSaveLoading(true);
    try {
      await generalApi.createQrCode({
        title: createForm.title.trim(),
        target_url: createForm.targetUrl.trim(),
      });
      setCreateOpen(false);
      setLoading(true);
      await loadRecords();
    } catch (err) {
      setFormError(err.message || 'QR kod oluşturulamadı.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const urlError = validateUrl(editForm.targetUrl);
    if (urlError) {
      setFormError(urlError);
      return;
    }
    setFormError('');
    setSaveLoading(true);
    try {
      await generalApi.updateLinkQrCode(editForm.id, {
        title: editForm.title.trim(),
        target_url: editForm.targetUrl.trim(),
      });
      setEditOpen(false);
      setLoading(true);
      await loadRecords();
    } catch (err) {
      setFormError(err.message || 'Kayıt güncellenemedi.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async (record) => {
    const label = record.title || record.target_url;
    if (!window.confirm(`"${label}" kaydını silmek istediğinize emin misiniz?`)) return;
    try {
      await generalApi.deleteLinkQrCode(record.id);
      setLoading(true);
      await loadRecords();
    } catch (err) {
      setError(err.message || 'Silme başarısız.');
    }
  };

  const handleDownload = (imageBase64, code) => {
    if (!imageBase64) return;
    const link = document.createElement('a');
    link.href = imageBase64;
    link.download = `qr-${code || 'link'}.png`;
    link.click();
  };

  const handleCopyLink = async (url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      alert('Link kopyalandı.');
    } catch {
      alert('Kopyalama başarısız.');
    }
  };

  if (loading && records.length === 0) {
    return <Loading fullScreen message="Yükleniyor..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <QrCodeIcon className="w-8 h-8 text-blue-600" />
              Link QR Kodları
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Link QR oluşturun, kaydedin ve daha sonra düzenleyin. Taratıldığında hedef URL açılır.
            </p>
          </div>
          <Button onClick={openCreate} className="inline-flex items-center gap-2">
            <PlusIcon className="w-5 h-5" />
            Yeni QR
          </Button>
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-700 dark:text-red-300 flex justify-between items-center">
            <span>{error}</span>
            <button type="button" onClick={() => setError('')} className="text-red-500 hover:text-red-700 ml-2">
              ×
            </button>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {records.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
              Henüz kayıtlı Link QR yok. Yeni QR ile başlayın.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Başlık</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Hedef URL</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Oluşturulma</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {records.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                        {record.title || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 max-w-xs truncate" title={record.target_url}>
                        {record.target_url}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {formatDate(record.created_at)}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => openPreview(record)}
                            className="p-1.5 text-gray-500 hover:text-blue-600 dark:hover:text-highlight-400"
                            title="QR Göster"
                          >
                            <QrCodeIcon className="w-5 h-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => openEdit(record)}
                            className="p-1.5 text-gray-500 hover:text-blue-600 dark:hover:text-highlight-400"
                            title="Düzenle"
                          >
                            <PencilSquareIcon className="w-5 h-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(record)}
                            className="p-1.5 text-gray-500 hover:text-red-600"
                            title="Sil"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ResponsiveModal isOpen={createOpen} onClose={() => setCreateOpen(false)} title="Yeni Link QR">
        <form onSubmit={handleCreate} className="space-y-4">
          <div className="space-y-1">
            <Label>Başlık (isteğe bağlı)</Label>
            <Input
              value={createForm.title}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="Örn. Restoran menüsü"
              disabled={saveLoading}
            />
          </div>
          <div className="space-y-1">
            <Label>Hedef URL *</Label>
            <Input
              type="url"
              value={createForm.targetUrl}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, targetUrl: e.target.value }))}
              placeholder="https://example.com"
              disabled={saveLoading}
            />
          </div>
          {formError && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-700 dark:text-red-300 text-sm">
              {formError}
            </div>
          )}
          <div className="mt-6 flex items-center justify-end space-x-3">
            <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} disabled={saveLoading}>
              İptal
            </Button>
            <Button type="submit" disabled={saveLoading}>
              {saveLoading ? 'Kaydediliyor...' : 'Oluştur'}
            </Button>
          </div>
        </form>
      </ResponsiveModal>

      <ResponsiveModal isOpen={editOpen} onClose={() => setEditOpen(false)} title="Link QR Düzenle">
        <form onSubmit={handleUpdate} className="space-y-4">
          <div className="space-y-1">
            <Label>Başlık (isteğe bağlı)</Label>
            <Input
              value={editForm.title}
              onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))}
              disabled={saveLoading}
            />
          </div>
          <div className="space-y-1">
            <Label>Hedef URL *</Label>
            <Input
              type="url"
              value={editForm.targetUrl}
              onChange={(e) => setEditForm((prev) => ({ ...prev, targetUrl: e.target.value }))}
              disabled={saveLoading}
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            URL değiştiğinde QR görseli yeni adrese göre güncellenir. Kod aynı kalır.
          </p>
          {formError && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-700 dark:text-red-300 text-sm">
              {formError}
            </div>
          )}
          <div className="mt-6 flex items-center justify-end space-x-3">
            <Button type="button" variant="outline" onClick={() => setEditOpen(false)} disabled={saveLoading}>
              İptal
            </Button>
            <Button type="submit" disabled={saveLoading}>
              {saveLoading ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </div>
        </form>
      </ResponsiveModal>

      <ResponsiveModal isOpen={previewOpen} onClose={() => setPreviewOpen(false)} title="QR Önizleme">
        {previewData?.loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">QR yükleniyor...</p>
        ) : previewData?.error ? (
          <p className="text-sm text-red-600 dark:text-red-300">{previewData.error}</p>
        ) : previewData ? (
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              {previewData.imageBase64 ? (
                <img
                  src={previewData.imageBase64}
                  alt="QR Code"
                  className="w-48 h-48 rounded-lg border border-gray-200 dark:border-gray-600"
                />
              ) : (
                <div className="w-48 h-48 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center text-sm text-gray-400">
                  QR yok
                </div>
              )}
            </div>
            <div className="flex-1 space-y-3 w-full">
              {previewData.title && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Başlık</p>
                  <p className="text-sm text-gray-900 dark:text-white">{previewData.title}</p>
                </div>
              )}
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Hedef Link</p>
                <p className="text-sm text-gray-900 dark:text-gray-100 break-all">{previewData.targetUrl || previewData.target_url}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleDownload(previewData.imageBase64, previewData.code)}
                  disabled={!previewData.imageBase64}
                  className="inline-flex items-center gap-2"
                >
                  <ArrowDownTrayIcon className="w-4 h-4" />
                  İndir
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleCopyLink(previewData.targetUrl || previewData.target_url)}
                  className="inline-flex items-center gap-2"
                >
                  <ClipboardDocumentIcon className="w-4 h-4" />
                  Linki Kopyala
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </ResponsiveModal>
    </div>
  );
};

export default QrCodePage;
